import useApiHelpers from '@/composables/api/helpers';
import useOrderEndpoints from '@/composables/api/useOrderEndpoints';
import {
  setGlobalContextProperty,
  setUser as setDataDogUser,
  addFeatureFlagEvaluation,
  addAction,
} from '@/composables/tracking/useConversionTracking';
import { logCustomerId, logOrderKey } from '@/composables/tracking/useGoogleAnalyticTracking';

/**
 * Tracks a customer in datadog
 * @param {string} customerId customer id
 * @returns
 */
export const trackCustomer = (customerId) => {
  if (!customerId) {
    return;
  }

  logCustomerId(customerId);
  setDataDogUser({ id: customerId });
};

/**
 * Tracks an order key in datadog
 * @param {string} orderKey order key
 * @returns
 */
export const trackOrderKey = (orderKey) => {
  if (!orderKey) {
    return;
  }

  setGlobalContextProperty('orderKey', orderKey);
  logOrderKey(orderKey);
};

/**
 * Tracks a custom property in datadog
 * @param {string} key key
 * @param {string} value value
 * @returns
 */
export const trackCustomProperty = (key, value) => {
  if (!key || !value) {
    return;
  }

  setGlobalContextProperty(key, value);
};

/**
 * Tracks a feature flag in datadog
 * @param {string} featureName feature name
 * @param {boolean} value feature value
 * @returns
 */
export const trackFeatureFlag = (featureName, value) => {
  if (!featureName || value === undefined) {
    return;
  }

  addFeatureFlagEvaluation(featureName, value);
};

/**
 * Tracks an action in datadog
 * @param {string} actionName action name
 * @param {object} payload action payload
 * @returns
 */
export const trackAction = (actionName, payload = {}) => {
  if (!actionName) {
    return;
  }
  /* eslint no-empty: ["error", { "allowEmptyCatch": true }] */
  try {
    addAction(actionName.toString(), payload);
  } catch (error) { }
};

/**
 * Posts an order event for tracking
 * @param {string} eventKey event key
 * @param {object} options options
 * @param {string|number} options.orderId order id (optional, will be fetched if not provided)
 * @param {object} options.data optional event data
 * @returns
 */
export const trackOrderEvent = (eventKey, options = {}) => {
  if (!eventKey) {
    return;
  }

  const { postRequest } = useApiHelpers();
  const { getOrderId } = useOrderEndpoints();
  const { orderId: providedOrderId, data } = options;
  const payload = {
    event: eventKey,
  };

  if (data && typeof data === 'object') {
    payload.data = data;
  }

  const sendRequest = (orderId) => {
    if (orderId) {
      payload.order_id = orderId;
    }

    /* eslint no-empty: ["error", { "allowEmptyCatch": true }] */
    postRequest('/order/event', payload, false, () => null, {}, false).catch(() => {
      // Silently ignore errors
    });
  };

  if (providedOrderId) {
    sendRequest(providedOrderId);
  } else {
    getOrderId()
      .then((orderId) => {
        sendRequest(orderId);
      })
      .catch(() => {
        sendRequest(null);
      });
  }
};
